import { getMelodicParentCategory, StemType } from '@arpeggi/shared'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import { LOOP_COLORS } from '../../lib/stitches'
import { IClipStore } from '../../stores/song/clip/ClipStore'
import { IStemStore } from '../../stores/song/StemStore'

export const gray15 = '#141414'
export const gray14 = '#292929'
export const gray13 = '#333333'
export const gray12 = '#3E3E3E'
export const gray11 = '#474747'
export const gray10 = '#5C5C5C'
export const gray9 = '#707070'
export const gray8 = '#8F8F8F'
export const gray7 = '#A3A3A3'
export const gray6 = '#C2C2C2'
export const gray5 = '#CCCCCC'
export const gray4 = '#E0E0E0'
export const gray3 = '#EBEBEB'
export const gray2 = '#F3F3F3'
export const gray1 = '#FFFFFF'

export const logoYellow = '#EABA42'
export const logoPink = '#F35083'
export const logoBlue = '#4992E8'

export const blue1 = '#ADCEFF'
export const blue2 = '#92C2FF'
export const blue3 = '#70A9FF'
export const blue4 = '#5C9DFF'
export const blue5 = '#4791FF'

export const green1 = '#ABD8B3'
export const green2 = '#9DD2A7'
export const green3 = '#81C58E'
export const green4 = '#73BF81'
export const green5 = '#65B875'

export const greenyellow1 = '#D0E1A3'
export const greenyellow2 = '#C9DC93'
export const greenyellow3 = '#C1D784'
export const greenyellow4 = '#B9D274'
export const greenyellow5 = '#B1CD65'

export const yellow1 = '#F0D07F'
export const yellow2 = '#EEC96D'
export const yellow3 = '#ECC35B'
export const yellow4 = '#EABC48'
export const yellow5 = '#E7B536'

export const orange1 = '#F5BD8E'
export const orange2 = '#F4B27C'
export const orange3 = '#F2A769'
export const orange4 = '#F09B56'
export const orange5 = '#EF9043'

export const red1 = '#FB9B88'
export const red2 = '#FA8B75'
export const red3 = '#F97B62'
export const red4 = '#F96A4E'
export const red5 = '#F85A3A'

export const pink1 = '#F88CAA'
export const pink2 = '#F7789C'
export const pink3 = '#F6658E'
export const pink4 = '#F55180'
export const pink5 = '#F43E72'

export const pinkpurple1 = '#EE95DA'
export const pinkpurple2 = '#EB84D5'
export const pinkpurple3 = '#E873CF'
export const pinkpurple4 = '#E561C9'
export const pinkpurple5 = '#E250C3'

export const purple1 = '#D1A7F1'
export const purple2 = '#C895EE'
export const purple3 = '#BF83EC'
export const purple4 = '#B572E9'
export const purple5 = '#AC60E6'

export const purpleblue1 = '#9FA8F9'
export const purpleblue2 = '#8C97F8'
export const purpleblue3 = '#7985F6'
export const purpleblue4 = '#6674F5'
export const purpleblue5 = '#5263F4'

// maps an instrument category to its corresponding color declarations
export const ColorMap = {
  Drums: [blue1, blue2, blue3, blue4, blue5],
  Percussion: [blue1, blue2, blue3, blue4, blue5],
  SFX: [green1, green2, green3, green4, green5],
  Vocals: [greenyellow1, greenyellow2, greenyellow3, greenyellow4, greenyellow5],
  Synth: [yellow1, yellow2, yellow3, yellow4, yellow5],
  Keys: [orange1, orange2, orange3, orange4, orange5],
  Guitar: [red1, red2, red3, red4, red5],
  Bass: [pink1, pink2, pink3, pink4, pink5],
  Strings: [pinkpurple1, pinkpurple2, pinkpurple3, pinkpurple4, pinkpurple5],
  Wind: [purple1, purple2, purple3, purple4, purple5],
  Misc: [purpleblue1, purpleblue2, purpleblue3, purpleblue4, purpleblue5]
}

export const ColorLevel1 = [
  blue1,
  green1,
  greenyellow1,
  yellow1,
  orange1,
  red1,
  pink1,
  pinkpurple1,
  purple1,
  purpleblue1
]

const CLIP_DEFAULT_COLOR = gray2

export const getCategoryColorsArr = (category: string) => {
  const colors = ColorMap[category] || ColorMap[getMelodicParentCategory(category)]
  return colors ? colors : ColorMap.Drums
}

/**
 * returns the color for this sample category to display as
 * */
export const getSampleCategoryColor = (category: string): string => {
  const colorsArr = getCategoryColorsArr(category) || ColorMap.Drums
  const index = colorsArr.length - 3
  return colorsArr[Math.abs(index) % colorsArr.length]
}

/**
 * assumes the ArpRegistry has already loaded the sample,
 * returns the color for this sample to display as
 * */
export const getSampleColor = (sampleIndex: number): string => {
  const category = ArpRegistry.retrieveFromCache(sampleIndex)?.category
  return getSampleCategoryColor(category)
}

/**
 * returns the color for this clip to display as based on a hash of its uniqueId
 * */
export const getClipColor = (clip: IClipStore): string => {
  if (!clip) return CLIP_DEFAULT_COLOR
  let clipNumber = 0
  clip.uniqueId.split('').forEach((char) => (clipNumber += char.charCodeAt(0)))
  return LOOP_COLORS[clipNumber % LOOP_COLORS.length]
}

/**
 * returns the color for this stem to display as based on its sample, or null if the sample hasn't been fetched
 * yet.
 * */
export const getStemColor = (stem: IStemStore): string => {
  // Special case drums, since it's possible it doesn't have a current sample
  // also special case for audio stems since they don't have samples
  if (stem.type() === StemType.DRUMS) return ColorMap.Drums[ColorMap.Drums.length - 3]
  if (stem.type() === StemType.AUDIO) return CLIP_DEFAULT_COLOR

  const sample = stem.getCurrentSample().sampleIndex
  if (sample <= 0) return null
  return getSampleColor(sample)
}

///////////////////// MINTING ////////////////////

export const MINTABLE_COLORS_MAP = {
  '#517CCE': {
    MEDIUM: '#6B9AF4',
    LIGHT: '#B8D0FE'
  }, // dark blue
  '#2A88E0': {
    MEDIUM: '#5DAEF9',
    LIGHT: '#A7D4FE'
  }, // blue
  '#31C2A8': {
    MEDIUM: '#4AD1B9',
    LIGHT: '#BEEFE6'
  }, // blue/green
  '#3CC236': {
    MEDIUM: '#62D15D',
    LIGHT: '#BDEABB'
  }, // green
  '#DBB424': {
    MEDIUM: '#ECC94B',
    LIGHT: '#FFE996'
  }, // mustard
  '#F28016': {
    MEDIUM: '#F49A30',
    LIGHT: '#FED9A2'
  }, // orange
  '#E96830': {
    MEDIUM: '#F58657',
    LIGHT: '#FECDB8'
  }, // orange/red
  '#E14040': {
    MEDIUM: '#F04F4F',
    LIGHT: '#FEB8B8'
  }, // red
  '#DD43E0': {
    MEDIUM: '#E567E8',
    LIGHT: '#F4B8FE'
  }, // pink/purple
  '#9953F2': {
    MEDIUM: '#B378FD',
    LIGHT: '#D7B8FE'
  }, // purple
  '#585FFC': {
    MEDIUM: '#787DFA',
    LIGHT: '#B8BBFE'
  }, // purple/blue
  '#292929': {
    MEDIUM: '#3E3E3E',
    LIGHT: '#CCCCCC'
  } // midnight
}

export const MINTABLE_COLORS = Object.keys(MINTABLE_COLORS_MAP)
